import React, {FC, useCallback} from 'react';
import {useIsInitialServerRequest} from './context';
import {
  useRenderingPrioritization,
  useShouldLoadingView,
  useShouldShowDataView,
} from './RenderingPrioritizer';
import {isValidationError} from './throwable';
import {DataFetcherRequest} from './useRequest';

interface DataFetcherProps<Data extends object, ViewProps extends object> {
  request: DataFetcherRequest<Data>;
  Loading?: React.ComponentType;
  Error: React.ComponentType<React.PropsWithChildren<{error: unknown}>> | null;
  View?: React.ComponentType<Data & ViewProps>;
  viewProps?: React.ComponentProps<React.ComponentType<ViewProps>>;
  /**
   * Allows to define a default state which is passed down while data fetcher is loading.
   * The loading component (e.g. skeleton) is not shown if a default state exists.
   */
  computeDefaultState?: () => Data | undefined;

  /**
   * renderingPriority: the priority in which the component should be rendered.
   * This number is relative to the other components rendered by the data fetcher in the tree.
   * adding renderingPriority here queues the component to be rendered based on that priority when the request resolves.
   * the priority set here is added to RenderingPrioritizerContext to be considered when rendering the components one by one
   */
  renderingPriority?: number;
  /**
   * By default, the idle state is also considered the loading state. In some situations this is not expected, so this flag can turn the default off
   */
  disableLoadingInIdleState?: boolean;
}

const DefaultFallbackComponent = () => null;

export const DataFetcher = <Data extends object, ViewProps extends object>({
  request,
  Loading: LoadingComponent = DefaultFallbackComponent,
  Error: ErrorComponent = DefaultFallbackComponent,
  View: ViewComponent = DefaultFallbackComponent,
  viewProps,
  computeDefaultState,
  renderingPriority,
  disableLoadingInIdleState,
}: DataFetcherProps<Data, ViewProps>): ReturnType<FC<Data & ViewProps>> => {
  const isInitialServerRequest =
    // eslint-disable-next-line react-hooks/rules-of-hooks
    !process.browser && useIsInitialServerRequest();
  const StaleViewComponent = useCallback(
    ViewComponent as any,
    [],
  ) as typeof ViewComponent;

  if (isInitialServerRequest) {
    return null;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useRenderingPrioritization({
    renderingPriority,
    request,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const showViewComponent = useShouldShowDataView({
    renderingPriority,
    request,
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const showLoadingComponent = useShouldLoadingView({
    renderingPriority,
    request,
    disableLoadingInIdleState,
  });

  // show error message after retries have finished
  if (request.error && !request.isLoading) {
    const error = isValidationError(request.error)
      ? request.error.data
      : request.error;

    return ErrorComponent ? <ErrorComponent error={error} /> : null;
  }

  if (showViewComponent) {
    return (
      <StaleViewComponent {...request.data!} {...(viewProps as ViewProps)} />
    );
  }

  if (computeDefaultState) {
    const defaultState = computeDefaultState();
    if (defaultState) {
      return (
        <StaleViewComponent {...defaultState} {...(viewProps as ViewProps)} />
      );
    }
  }

  if (LoadingComponent && showLoadingComponent) {
    return <LoadingComponent />;
  }

  return null;
};
